import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import {
  createExpense,
  createExpenseType,
  listExpenseTypes,
  createExpenseFund,
  listExpenseFunds,
} from "../services/expense";
import { formatCurrencyMZN } from "../utils/paymentHelpers";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 2;
const todayIso = () => new Date().toISOString().split("T")[0];
const toDMY = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const emptyItem = () => ({ description: "", price: "" });

// "Pagamento de Fornecedores" continua guardada como Variável em
// SGE_EXPENSE_TYPES (não duplicamos a categoria na BD), mas também deve
// poder ser escolhida ao registar uma despesa Fixa - ver categoriesForKind.
const SUPPLIER_PAYMENT_CATEGORY = "Pagamento de Fornecedores";

// Fundo de Despesa (SGE_EXPENSE_FUNDS) pré-seleccionado por omissão ao abrir
// o wizard - ver `fetchExpenseFunds`/effect abaixo. Continua a ser possível
// escolher ou criar outro fundo, tal como já acontece com a Categoria.
const DEFAULT_FUND_NAME = "Pagamento de Propinas";

const initialState = {
  expenseKind: "", // 'FIXA' | 'VARIAVEL' - ver despesa.kind.fixed/variable
  expenseName: "", // Nome/Designação - livre, já não é igual à categoria
  expenseType: "", // categoria seleccionada (nome em SGE_EXPENSE_TYPES)
  expenseFund: "", // fundo seleccionado (nome em SGE_EXPENSE_FUNDS) - ver DEFAULT_FUND_NAME
  expenseValue: "", // só para despesas Fixas - Variáveis usam o somatório de `items`
  expenseDate: todayIso(),
  notes: "",
};

// Split out of the former DespesasLayer. Same "hide cadastro from the menu"
// philosophy applied to ListaProdutosLayer/CadastrarProdutoLayer — this
// screen is only reachable via the "Adicionar Despesa" button on
// ListaDespesasLayer, not from the sidebar.
//
// Tipo de Despesa (Fixa/Variável) escolhido primeiro decide que categorias
// aparecem (SGE_EXPENSE_TYPES filtrado por `kind` - vem sempre da BD via
// listExpenseTypes()) e como o Valor é preenchido: manual para Fixa, ou o
// somatório de uma tabela editável de itens (Descrição + Preço) para
// Variável - a mesma lista que a Listagem/relatório PDF mostram ao
// expandir a despesa (ver ListaDespesasLayer).
//
// Escolher "+ Nova categoria" cria o tipo de imediato via createExpenseType
// (confirmNewCategory), para esta sucursal e já com o `kind` escolhido -
// fica logo disponível em categoriesForKind, mesmo que o utilizador acabe
// por não submeter esta despesa.
//
// "Pagamento de Fornecedores" é a única excepção a esse filtro por `kind`:
// continua guardada como Variável em SGE_EXPENSE_TYPES, mas categoriesForKind
// injecta-a também na lista de Fixas, para poder ser usada nos dois casos.
const CadastrarDespesaLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [items, setItems] = useState([emptyItem()]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [expenseFunds, setExpenseFunds] = useState([]);
  const [addingFund, setAddingFund] = useState(false);
  const [newFundName, setNewFundName] = useState("");
  const [creatingFund, setCreatingFund] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("despesa.wizard.step.data"), t("despesa.wizard.step.confirm")];

  const fetchExpenseTypes = () =>
    listExpenseTypes().then((resp) => setExpenseTypes(resp.data || [])).catch(() => setExpenseTypes([]));

  const fetchExpenseFunds = () =>
    listExpenseFunds().then((resp) => setExpenseFunds(resp.data || [])).catch(() => setExpenseFunds([]));

  useEffect(() => {
    fetchExpenseTypes();
    fetchExpenseFunds();
  }, []);

  // Pré-selecciona "Pagamento de Propinas" assim que os fundos carregam -
  // só quando ainda não há nada escolhido, para não sobrepor uma escolha do
  // utilizador nem sobreviver a um `restart()` inesperadamente diferente.
  useEffect(() => {
    if (form.expenseFund || expenseFunds.length === 0) return;
    const defaultFund = expenseFunds.find((f) => f.name === DEFAULT_FUND_NAME);
    if (defaultFund) setForm((f) => (f.expenseFund ? f : { ...f, expenseFund: defaultFund.name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseFunds]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const categoriesForKind = useMemo(() => {
    const matches = expenseTypes.filter((et) => et.kind === form.expenseKind);
    // Exceção: "Pagamento de Fornecedores" aparece também nas Fixas, além de
    // continuar nas Variáveis (onde já vive na BD) - ver SUPPLIER_PAYMENT_CATEGORY.
    if (form.expenseKind === "FIXA" && !matches.some((et) => et.name === SUPPLIER_PAYMENT_CATEGORY)) {
      const supplierPayment = expenseTypes.find((et) => et.name === SUPPLIER_PAYMENT_CATEGORY);
      if (supplierPayment) matches.push(supplierPayment);
    }
    return matches;
  }, [expenseTypes, form.expenseKind]);

  const itemsTotal = useMemo(
    () => items.reduce((sum, i) => sum + (Number(i.price) || 0), 0),
    [items],
  );

  const validItems = () =>
    items
      .filter((i) => i.description.trim() && Number(i.price) > 0)
      .map((i) => ({ description: i.description.trim(), price: Number(i.price) }));

  const handleKindChange = (e) => {
    const expenseKind = e.target.value;
    setForm((f) => ({ ...f, expenseKind, expenseType: "", expenseValue: "" }));
    setItems([emptyItem()]);
    setAddingCategory(false);
    setNewCategoryName("");
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "__new__") {
      setAddingCategory(true);
      setForm((f) => ({ ...f, expenseType: "" }));
      return;
    }
    setAddingCategory(false);
    setForm((f) => ({ ...f, expenseType: value }));
  };

  // Creates the category for this sucursal right away (instead of only when
  // the whole despesa is submitted, as before), so it's immediately real —
  // reusable by other despesas from this point on, and visible if the user
  // abandons the wizard before step 2. Also means `expenseTypeId` below can
  // be resolved on submit like any other category, instead of always being
  // left unmapped for a brand-new one.
  const confirmNewCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    if (expenseTypes.some((et) => et.name === name)) {
      toast.error(t("despesa.error.categoryExists"));
      return;
    }
    setCreatingCategory(true);
    try {
      const resp = await createExpenseType(name, undefined, form.expenseKind);
      const created = resp.data?.data;
      setExpenseTypes((prev) => [...prev, created]);
      setForm((f) => ({ ...f, expenseType: created?.name || name }));
      setAddingCategory(false);
      setNewCategoryName("");
      toast.success(t("despesa.success.typeCreated"));
    } catch (err) {
      toast.error(err?.response?.data?.error || t("despesa.error.createType"));
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleFundChange = (e) => {
    const value = e.target.value;
    if (value === "__new__") {
      setAddingFund(true);
      setForm((f) => ({ ...f, expenseFund: "" }));
      return;
    }
    setAddingFund(false);
    setForm((f) => ({ ...f, expenseFund: value }));
  };

  // Mesma lógica de confirmNewCategory acima, mas para Fundos de Despesa
  // (SGE_EXPENSE_FUNDS) - criado de imediato para esta sucursal, já
  // disponível em expenseFunds mesmo que o utilizador acabe por não
  // submeter esta despesa.
  const confirmNewFund = async () => {
    const name = newFundName.trim();
    if (!name) return;
    if (expenseFunds.some((f) => f.name === name)) {
      toast.error(t("despesa.error.fundExists"));
      return;
    }
    setCreatingFund(true);
    try {
      const resp = await createExpenseFund(name);
      const created = resp.data?.data;
      setExpenseFunds((prev) => [...prev, created]);
      setForm((f) => ({ ...f, expenseFund: created?.name || name }));
      setAddingFund(false);
      setNewFundName("");
      toast.success(t("despesa.success.fundCreated"));
    } catch (err) {
      toast.error(err?.response?.data?.error || t("despesa.error.createFund"));
    } finally {
      setCreatingFund(false);
    }
  };

  const updateItem = (index, field) => (e) => {
    const value = e.target.value;
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };
  const addItemRow = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItemRow = (index) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const nextStep = () => {
    if (
      !form.expenseKind ||
      !form.expenseName.trim() ||
      !form.expenseType.trim() ||
      !form.expenseFund.trim() ||
      !form.expenseDate
    ) {
      toast.error(t("despesa.error.requiredFields"));
      return;
    }
    if (form.expenseKind === "FIXA" && !form.expenseValue) {
      toast.error(t("despesa.error.requiredValue"));
      return;
    }
    if (form.expenseKind === "VARIAVEL" && validItems().length === 0) {
      toast.error(t("despesa.error.requiredItems"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setItems([emptyItem()]);
    setAddingCategory(false);
    setNewCategoryName("");
    setAddingFund(false);
    setNewFundName("");
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // year/month are stored (and indexed by a GSI) as strings on this table —
      // sending numbers throws a DynamoDB "Type mismatch ... Expected: S Actual: N".
      const [year, month] = form.expenseDate.split("-");
      const expenseType = form.expenseType.trim();
      // id estável em SGE_EXPENSE_TYPES, usado pelo Mapeamento de Despesas na
      // Contabilidade para saber que conta lançar. Uma categoria nova já foi
      // criada em confirmNewCategory antes deste passo, por isso já existe
      // aqui como qualquer outra - nunca fica por mapear só por ser nova.
      const matchedType = expenseTypes.find((et) => et.name === expenseType);
      const expenseFund = form.expenseFund.trim();
      // id estável em SGE_EXPENSE_FUNDS - mesmo raciocínio de expenseTypeId
      // acima, aplicado ao Fundo de Despesa.
      const matchedFund = expenseFunds.find((ef) => ef.name === expenseFund);
      const finalItems = form.expenseKind === "VARIAVEL" ? validItems() : [];
      const expenseValue =
        form.expenseKind === "VARIAVEL" ? finalItems.reduce((sum, i) => sum + i.price, 0) : Number(form.expenseValue);

      await createExpense({
        expenseName: form.expenseName.trim(),
        expenseType,
        expenseTypeId: matchedType?.id,
        expenseFund,
        expenseFundId: matchedFund?.id,
        expenseKind: form.expenseKind,
        expenseValue,
        items: finalItems,
        expenseDate: toDMY(form.expenseDate),
        notes: form.notes,
        month: String(Number(month)),
        year,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || t("despesa.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("despesa.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("despesa.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  {/* Tipo/Categoria/Fundo e Nome/Valor/Data em col-lg-4 (em
                      vez de col-sm-6) para caberem 3 por linha em ecrãs
                      grandes, dando espaço à nova coluna Fundo de Despesa
                      sem alongar o wizard verticalmente. */}
                  <div className='row gy-3'>
                    <div className='col-sm-6 col-lg-4'>
                      <label className='form-label'>{t("despesa.field.type")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:widget-outline' />
                        </span>
                        <select className='form-control form-select' value={form.expenseKind} onChange={handleKindChange} required>
                          <option value='' disabled>
                            —
                          </option>
                          <option value='FIXA'>{t("despesa.kind.fixed")}</option>
                          <option value='VARIAVEL'>{t("despesa.kind.variable")}</option>
                        </select>
                      </div>
                    </div>

                    <div className='col-sm-6 col-lg-4'>
                      <label className='form-label'>{t("despesa.field.category")}</label>
                      {!addingCategory ? (
                        <div className='icon-field'>
                          <span className='icon'>
                            <Icon icon='solar:box-minimalistic-outline' />
                          </span>
                          <select
                            className='form-control form-select'
                            value={form.expenseType}
                            onChange={handleCategoryChange}
                            disabled={!form.expenseKind}
                            required
                          >
                            <option value='' disabled>
                              {t("despesa.field.category.placeholder")}
                            </option>
                            {categoriesForKind.map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                            <option value='__new__'>{t("despesa.field.category.newOption")}</option>
                          </select>
                        </div>
                      ) : (
                        <div className='d-flex gap-8'>
                          <input
                            type='text'
                            className='form-control'
                            placeholder={t("despesa.field.category.new.placeholder")}
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            disabled={creatingCategory}
                            autoFocus
                          />
                          <button
                            type='button'
                            className='btn btn-primary-600 px-16'
                            onClick={confirmNewCategory}
                            disabled={creatingCategory}
                          >
                            <Icon icon={creatingCategory ? "svg-spinners:ring-resize" : "mdi:check-bold"} />
                          </button>
                          <button
                            type='button'
                            className='btn btn-neutral-500 border-neutral-100 px-16'
                            onClick={() => {
                              setAddingCategory(false);
                              setNewCategoryName("");
                            }}
                            disabled={creatingCategory}
                          >
                            <Icon icon='mdi:close-circle-outline' />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className='col-sm-6 col-lg-4'>
                      <label className='form-label'>{t("despesa.field.fund")}</label>
                      {!addingFund ? (
                        <div className='icon-field'>
                          <span className='icon'>
                            <Icon icon='solar:wallet-outline' />
                          </span>
                          <select
                            className='form-control form-select'
                            value={form.expenseFund}
                            onChange={handleFundChange}
                            required
                          >
                            <option value='' disabled>
                              {t("despesa.field.fund.placeholder")}
                            </option>
                            {expenseFunds.map((f) => (
                              <option key={f.id} value={f.name}>
                                {f.name}
                              </option>
                            ))}
                            <option value='__new__'>{t("despesa.field.fund.newOption")}</option>
                          </select>
                        </div>
                      ) : (
                        <div className='d-flex gap-8'>
                          <input
                            type='text'
                            className='form-control'
                            placeholder={t("despesa.field.fund.new.placeholder")}
                            value={newFundName}
                            onChange={(e) => setNewFundName(e.target.value)}
                            disabled={creatingFund}
                            autoFocus
                          />
                          <button
                            type='button'
                            className='btn btn-primary-600 px-16'
                            onClick={confirmNewFund}
                            disabled={creatingFund}
                          >
                            <Icon icon={creatingFund ? "svg-spinners:ring-resize" : "mdi:check-bold"} />
                          </button>
                          <button
                            type='button'
                            className='btn btn-neutral-500 border-neutral-100 px-16'
                            onClick={() => {
                              setAddingFund(false);
                              setNewFundName("");
                            }}
                            disabled={creatingFund}
                          >
                            <Icon icon='mdi:close-circle-outline' />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className='col-sm-6 col-lg-4'>
                      <label className='form-label'>{t("despesa.field.name")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='f7:person' />
                        </span>
                        <input type='text' className='form-control' value={form.expenseName} onChange={setField("expenseName")} required />
                      </div>
                    </div>

                    {form.expenseKind === "FIXA" && (
                      <div className='col-sm-6 col-lg-4'>
                        <label className='form-label'>{t("despesa.field.value")}</label>
                        <div className='icon-field'>
                          <span className='icon'>
                            <Icon icon='solar:pen-2-outline' />
                          </span>
                          <input type='number' min='0' className='form-control' value={form.expenseValue} onChange={setField("expenseValue")} required />
                        </div>
                      </div>
                    )}

                    <div className='col-sm-6 col-lg-4'>
                      <label className='form-label'>{t("despesa.field.date")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:calendar-outline' />
                        </span>
                        <input type='date' className='form-control' value={form.expenseDate} onChange={setField("expenseDate")} required />
                      </div>
                    </div>

                    {form.expenseKind === "VARIAVEL" && form.expenseType && (
                      <div className='col-12'>
                        <label className='form-label'>{t("despesa.field.items")}</label>
                        <div className='table-responsive scroll-sm'>
                          <table className='table bordered-table mb-0'>
                            <thead>
                              <tr>
                                <th scope='col'>{t("despesa.field.items.description")}</th>
                                <th scope='col' style={{ width: 180 }}>
                                  {t("despesa.field.items.price")}
                                </th>
                                <th scope='col' style={{ width: 60 }} />
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <input
                                      type='text'
                                      className='form-control'
                                      value={item.description}
                                      onChange={updateItem(index, "description")}
                                    />
                                  </td>
                                  <td>
                                    <input type='number' min='0' className='form-control' value={item.price} onChange={updateItem(index, "price")} />
                                  </td>
                                  <td className='text-center'>
                                    <button
                                      type='button'
                                      className='text-danger-600 bg-transparent border-0'
                                      onClick={() => removeItemRow(index)}
                                      disabled={items.length === 1}
                                    >
                                      <Icon icon='mingcute:delete-2-line' />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td className='fw-semibold text-end' colSpan={1}>
                                  {t("despesa.field.items.total")}
                                </td>
                                <td className='fw-semibold'>{formatCurrencyMZN(itemsTotal)}</td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <button type='button' className='btn btn-sm btn-outline-primary mt-8' onClick={addItemRow}>
                          {t("despesa.field.items.add")}
                        </button>
                      </div>
                    )}

                    <div className='col-12'>
                      <label className='form-label'>{t("despesa.field.notes")}</label>
                      <textarea className='form-control' rows='2' value={form.notes} onChange={setField("notes")} />
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("despesa.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("despesa.field.type")}:</strong>{" "}
                      {form.expenseKind === "FIXA" ? t("despesa.kind.fixed") : t("despesa.kind.variable")}
                    </li>
                    <li>
                      <strong>{t("despesa.field.name")}:</strong> {form.expenseName}
                    </li>
                    <li>
                      <strong>{t("despesa.field.category")}:</strong> {form.expenseType}
                    </li>
                    <li>
                      <strong>{t("despesa.field.fund")}:</strong> {form.expenseFund}
                    </li>
                    <li>
                      <strong>{t("despesa.field.value")}:</strong>{" "}
                      {form.expenseKind === "FIXA" ? formatCurrencyMZN(form.expenseValue) : formatCurrencyMZN(itemsTotal)}
                    </li>
                    <li>
                      <strong>{t("despesa.field.date")}:</strong> {toDMY(form.expenseDate)}
                    </li>
                  </ul>

                  {form.expenseKind === "VARIAVEL" && (
                    <div className='table-responsive scroll-sm mt-16'>
                      <table className='table bordered-table mb-0'>
                        <thead>
                          <tr>
                            <th scope='col'>{t("despesa.field.items.description")}</th>
                            <th scope='col'>{t("despesa.field.items.price")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validItems().map((item, index) => (
                            <tr key={index}>
                              <td>{item.description}</td>
                              <td>{formatCurrencyMZN(item.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("despesa.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("despesa.action.createAnother")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/despesas/lista")}
                    >
                      <i className='ri-arrow-left-line' /> {t("despesa.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarDespesaLayer;
